import { trackedSet } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismTable, styleEntry } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import type { Hotelinfo } from 'frontend-utils/models';
import TdDateMonth from 'lumen/components/hotel/pricing-assistant/table/cells/td-date-month';
import TdRoomRateMonth from 'lumen/components/hotel/pricing-assistant/table/cells/td-room-rate-month';
import ThBaseRoomMonth from 'lumen/components/hotel/pricing-assistant/table/cells/th-base-room-month';
import type {
  TablePricingManagerContext,
  TablePricingManagerContextColumn,
} from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager';
import type { Carate, Period } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { isWeekendDate } from 'lumen/utils/charts/chart-utils';
import {
  DAY_TABLE_SIZES,
  HEADER_HEIGHT,
  SUB_HEADER_HEIGHT,
} from 'lumen/utils/constants/general-constants';
import { sortRoomCaratesByOffsetHierarchy } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';

import styles from './table-pricing-manager-month.module.css';

const DATE_COLUMN_WIDTH = 192;

// Resolves to nothing on every row, so the cell renders empty
const PLACEHOLDER_VALUE_PATH = 'placeholder';

interface MonthRow extends CustomEmberTableRow {
  className?: string;
  id: string;
  period: Period;
  rooms: Record<string, Carate | undefined>;
}

interface HotelPricingAssistantTableTablePricingManagerMonthSignature {
  Args: {
    competitorsForCurrentHotelAndOTA: Hotelinfo[] | null;
    flattenedRoomOffsetHierarchy: string[];
    isLoading: boolean;
    periods: Period[];
    rateCode: string | null;
    refDaysAgo: number;
  };
}

export default class HotelPricingAssistantTableTablePricingManagerMonth extends Component<HotelPricingAssistantTableTablePricingManagerMonthSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  datesInLoading = trackedSet<string>();

  get baseRoomName(): string | null | undefined {
    return this.hotelAndMarketService.hotel.caxxxPmsSettings
      .pms_leading_room_id;
  }

  /* Header rows: ember-table renders a second row for `subcolumns`. The date
     column has none, so it spans both rows. */
  get columns(): CustomEmberTableColumn[] {
    const columns: CustomEmberTableColumn[] = [
      {
        name: this.intl.t('general.date'),
        valuePath: 'period',
        isFixed: 'left',
        hasRightBorder: true,
        textAlign: 'left',
        width: DATE_COLUMN_WIDTH,
        tdComponent: TdDateMonth,
      },
      this.groupColumn(
        this.intl.t('pricing_manager.monthly_table.hotel_level'),
        [
          this.placeholderColumn(this.intl.t('pricing_assistant.my_OTB')),
          this.placeholderColumn(this.intl.t('general.forecast')),
          this.placeholderColumn(
            this.intl.t('pricing_manager.monthly_table.left_to_sell'),
          ),
          this.placeholderColumn(
            this.intl.t('pricing_manager.monthly_table.pickup_rooms'),
          ),
          this.placeholderColumn(
            this.intl.t('pricing_manager.monthly_table.overbookings'),
          ),
          this.placeholderColumn(
            this.intl.t('pricing_manager.monthly_table.restrictions'),
          ),
        ],
      ),
    ];

    const baseRoomIndex = this.roomCarates.findIndex(
      (carate) => carate.roomName === this.baseRoomName,
    );

    const baseRoomCarate = this.roomCarates[baseRoomIndex];

    if (baseRoomCarate) {
      columns.push(this.baseRoomGroupColumn(baseRoomCarate, baseRoomIndex));
    }

    this.roomCarates.forEach((carate, index) => {
      if (index !== baseRoomIndex) {
        columns.push(
          this.groupColumn(carate.roomName, [
            this.placeholderColumn(
              this.intl.t('pricing_manager.monthly_table.left_to_sell'),
            ),
            this.roomRateColumn(index),
          ]),
        );
      }
    });

    columns.push(
      this.groupColumn(
        this.intl.t('pricing_manager.monthly_table.restrictions'),
        [
          this.placeholderColumn(this.intl.t('pricing_assistant.label.rooms')),
          this.placeholderColumn(
            this.intl.t('pricing_assistant.stay_restrictions.rate_groups'),
          ),
        ],
      ),
    );

    return columns;
  }

  /* Rooms with a carate for the selected rate code on any day in the range, so a room missing on the first day still gets its columns. */
  @cached
  get roomCarates(): Carate[] {
    const caratesByRoomName = new Map<string, Carate>();

    for (const period of this.args.periods) {
      for (const carate of period.ownHotelCarates) {
        if (
          carate.rateCode === this.args.rateCode &&
          !caratesByRoomName.has(carate.roomName)
        ) {
          caratesByRoomName.set(carate.roomName, carate);
        }
      }
    }

    return sortRoomCaratesByOffsetHierarchy(
      Array.from(caratesByRoomName.values()),
      this.args.flattenedRoomOffsetHierarchy,
    );
  }

  @cached
  get rows(): MonthRow[] {
    if (this.args.isLoading) {
      return [];
    }

    return this.args.periods.map((period): MonthRow => {
      const ownHotelCarates = period.ownHotelCarates;
      const rooms: MonthRow['rooms'] = {};

      this.roomCarates.forEach((roomCarate, index) => {
        rooms[index] = ownHotelCarates.find(
          (carate) =>
            carate.roomName === roomCarate.roomName &&
            carate.rateCode === this.args.rateCode,
        );
      });

      return {
        // TODO(COA-4308): temporary Fri/Sat weekend, mirroring Rate Insight's isWeekendDate.
        // Replace with the hotel's weekend-day mapping from Settings, per spec:
        // https://www.figma.com/design/yx8TkjqaRngRguX0kSrJab/Multi-rate-table---Extended-view?node-id=1-17041&m=dev
        className: isWeekendDate(period.fromDate)
          ? styles['weekend-row']
          : undefined,
        id: period.fromDate.format('YYYY-MM-DD'),
        period,
        rooms,
      };
    });
  }

  @cached
  get tableContext(): TablePricingManagerContext {
    return {
      datesInLoading: this.datesInLoading,
      // Override actions come with the monthly edit modals follow-up
      deleteOverride: async (): Promise<void> => {},
      openEditOverrideModal: (): void => {},
      refDaysAgo: this.args.refDaysAgo,
    };
  }

  /* Reserve space for: 2 nav headers + sub-header + main content padding top + main content padding bottom */
  get tableReservedHeight(): string {
    return `${HEADER_HEIGHT + SUB_HEADER_HEIGHT + 16 + 16}px`;
  }

  baseRoomGroupColumn(carate: Carate, index: number): CustomEmberTableColumn {
    const competitors = this.args.competitorsForCurrentHotelAndOTA ?? [];

    return {
      ...this.groupColumn(carate.roomName, [
        this.placeholderColumn(
          this.intl.t('pricing_manager.monthly_table.left_to_sell'),
        ),
        this.placeholderColumn(
          this.intl.t('pricing_manager.monthly_table.overbookings'),
        ),
        this.placeholderColumn(
          this.intl.t('pricing_manager.monthly_table.restrictions'),
        ),
        this.roomRateColumn(index),
        ...competitors.map((competitor) =>
          this.placeholderColumn(competitor.name),
        ),
      ]),
      thComponent: ThBaseRoomMonth,
    };
  }

  groupColumn(
    name: string,
    subcolumns: CustomEmberTableColumn[],
  ): CustomEmberTableColumn {
    const lastSubcolumn = subcolumns[subcolumns.length - 1];

    if (lastSubcolumn) {
      lastSubcolumn.hasRightBorder = true;
    }

    return {
      name,
      valuePath: '',
      textAlign: 'center',
      hasRightBorder: true,
      subcolumns,
    };
  }

  placeholderColumn(name: string): CustomEmberTableColumn {
    return {
      name,
      valuePath: PLACEHOLDER_VALUE_PATH,
      textAlign: 'right',
      width: DAY_TABLE_SIZES.VALUE_WIDTH,
    };
  }

  // Keyed by index: room names can contain dots, which a value path would split
  roomRateColumn(index: number): CustomEmberTableColumn {
    return {
      name: this.intl.t('pricing_manager.monthly_table.room_rate'),
      valuePath: `rooms.${index}`,
      textAlign: 'right',
      width: DAY_TABLE_SIZES.VALUE_WIDTH,
      thClassNames: styles['room-rate-header'],
      tdComponent: TdRoomRateMonth,
      tableContext: this.tableContext,
    } satisfies CustomEmberTableColumn & TablePricingManagerContextColumn;
  }

  <template>
    <div
      class={{styles.table}}
      style={{styleEntry "--table-reserved-height" this.tableReservedHeight}}
    >
      <PrismTable
        @columns={{this.columns}}
        @isLoading={{@isLoading}}
        @nrOfEmptyRows={{31}}
        @nrOfLoadingRows={{5}}
        @rows={{this.rows}}
        @widthConstraint="gte-container-slack"
      />
    </div>
  </template>
}
